import type { ToolCall, ToolResult } from "../../types";
import type { DeckPlan, LibrarySlideCandidate, SlideOutline } from "./deckPlannerService";
import type { AgentToolContext } from "./types";
import {
  hydrateDeckPlanLibraryFields,
  libraryPickNudgeMessage,
  shouldNudgeLibraryPicks,
} from "./slideLibraryAgentTools";

const MAX_PLANNED_SLIDES = 20;

function assertPlannedSlideCount(count: number): void {
  if (count > MAX_PLANNED_SLIDES) {
    throw new Error(
      `"slides" must contain ${MAX_PLANNED_SLIDES} or fewer slide outlines; received ${count}.`
    );
  }
}

function asString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function asOptionalIndex(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return undefined;
  return Math.floor(value);
}

function parseLibraryCandidate(raw: unknown): LibrarySlideCandidate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const slideId = asString(record.slideId);
  if (!slideId) return null;
  const candidate: LibrarySlideCandidate = {
    slideId,
    title: asString(record.title) || "",
  };
  const description = asString(record.description);
  if (description) candidate.description = description;
  const tags = asStringArray(record.tags);
  if (tags) candidate.tags = tags;
  const thumbnailUrl = asString(record.thumbnailUrl);
  if (thumbnailUrl) candidate.thumbnailUrl = thumbnailUrl;
  const rankReason = asString(record.rankReason);
  if (rankReason) candidate.rankReason = rankReason;
  return candidate;
}

export type SlideOutlinePatch = Partial<SlideOutline> & { index: number };

function parseSlideIndex(record: Record<string, unknown>, fallbackIndex: number): number {
  return typeof record.index === "number" && Number.isFinite(record.index)
    ? Math.max(0, Math.floor(record.index))
    : fallbackIndex;
}

function clearLibraryPickFields(target: Partial<SlideOutline>): void {
  target.librarySlideTitle = undefined;
  target.librarySlideThumbnailUrl = undefined;
  target.librarySlideTags = undefined;
  target.librarySlideDescription = undefined;
  target.librarySlidePptxUrl = undefined;
  target.librarySlidePptxUrlCandidates = undefined;
  target.rememberedLibrarySlide = undefined;
}

function applyOptionalSlideFields(
  target: Partial<SlideOutline>,
  record: Record<string, unknown>
): void {
  if ("baseSlideIndex" in record) {
    const baseSlideIndex = asOptionalIndex(record.baseSlideIndex);
    target.baseSlideIndex = baseSlideIndex;
  }

  if ("librarySlideId" in record) {
    const librarySlideId = asString(record.librarySlideId);
    target.librarySlideId = librarySlideId;
    // Drop prior pick media whenever the id is set or cleared so merge cannot
    // keep the previous thumbnail/PPTX paired with a different librarySlideId.
    clearLibraryPickFields(target);
  }
  const librarySlideTitle = asString(record.librarySlideTitle);
  if (librarySlideTitle) target.librarySlideTitle = librarySlideTitle;
  const librarySlideThumbnailUrl = asString(record.librarySlideThumbnailUrl);
  if (librarySlideThumbnailUrl) target.librarySlideThumbnailUrl = librarySlideThumbnailUrl;
  const librarySlideTags = asStringArray(record.librarySlideTags);
  if (librarySlideTags) target.librarySlideTags = librarySlideTags;
  const librarySlideDescription = asString(record.librarySlideDescription);
  if (librarySlideDescription) target.librarySlideDescription = librarySlideDescription;

  if (Array.isArray(record.librarySlideCandidates)) {
    const candidates = record.librarySlideCandidates
      .map((candidate) => parseLibraryCandidate(candidate))
      .filter((candidate): candidate is LibrarySlideCandidate => Boolean(candidate));
    if (candidates.length > 0) target.librarySlideCandidates = candidates;
  }
}

export function parseSlideOutline(raw: unknown, fallbackIndex: number): SlideOutline | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const slide: SlideOutline = {
    index: parseSlideIndex(record, fallbackIndex),
    title: asString(record.title) || `Slide ${fallbackIndex + 1}`,
    bullets: asStringArray(record.bullets) ?? [],
  };
  applyOptionalSlideFields(slide, record);
  return slide;
}

/** Sparse `update_deck_plan` patch: omit title/bullets unless the model sent them. */
export function parseSlideOutlinePatch(
  raw: unknown,
  fallbackIndex: number
): SlideOutlinePatch | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const patch: SlideOutlinePatch = { index: parseSlideIndex(record, fallbackIndex) };
  const title = asString(record.title);
  if (title) patch.title = title;
  if (Array.isArray(record.bullets)) {
    patch.bullets = asStringArray(record.bullets) ?? [];
  }
  applyOptionalSlideFields(patch, record);
  return patch;
}

export function parseDeckPlanFromToolArgs(
  args: Record<string, unknown>,
  fallbackTopic?: string
): DeckPlan {
  const slidesRaw = args.slides;
  if (!Array.isArray(slidesRaw) || slidesRaw.length === 0) {
    throw new Error(`"slides" must be a non-empty array`);
  }
  assertPlannedSlideCount(slidesRaw.length);
  const slides = slidesRaw
    .map((slide, index) => parseSlideOutline(slide, index))
    .filter((slide): slide is SlideOutline => Boolean(slide))
    .map((slide, index) => ({ ...slide, index }));
  if (slides.length === 0) {
    throw new Error(`"slides" must contain at least one valid slide outline`);
  }
  return {
    topic: asString(args.topic) || fallbackTopic || "New deck",
    slideCount: slides.length,
    slides,
  };
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Overlay an incoming slide on the live Review slide. When `base` is the last
 * agent snapshot, titles/bullets the agent echoed unchanged keep the live
 * (user-edited) values so a library-pick update cannot clobber Review edits.
 */
export function mergeSlideOntoLive(
  live: SlideOutline | undefined,
  incoming: SlideOutline,
  index: number,
  base?: SlideOutline
): SlideOutline {
  if (!live) return { ...incoming, index };
  if (!base) {
    return { ...live, ...incoming, index };
  }
  const title = sameJson(incoming.title, base.title) ? live.title : incoming.title;
  const bullets = sameJson(incoming.bullets, base.bullets) ? live.bullets : incoming.bullets;
  return { ...live, ...incoming, index, title, bullets };
}

export function mergeDeckPlan(
  current: DeckPlan | undefined,
  next: { topic?: string; slides?: SlideOutline[]; patches?: SlideOutlinePatch[] },
  options?: { base?: DeckPlan }
): DeckPlan {
  if (next.slides && next.slides.length > 0) {
    assertPlannedSlideCount(next.slides.length);
    const slides = next.slides
      .map((slide, index) =>
        mergeSlideOntoLive(
          current?.slides[index],
          { ...slide, index },
          index,
          options?.base?.slides[index]
        )
      );
    return {
      ...(current ?? { topic: next.topic || "New deck", slideCount: slides.length, slides: [] }),
      topic: next.topic || current?.topic || "New deck",
      slideCount: slides.length,
      slides,
    };
  }

  const baseSlides = [...(current?.slides ?? [])];
  for (const patch of next.patches ?? []) {
    const index = Math.max(0, Math.floor(patch.index));
    const existing = baseSlides[index];
    if (!existing) continue;
    const title =
      typeof patch.title === "string" && patch.title.trim() ? patch.title.trim() : existing.title;
    const bullets = Array.isArray(patch.bullets) ? patch.bullets : existing.bullets;
    baseSlides[index] = {
      ...existing,
      ...patch,
      index,
      title,
      bullets,
    };
  }
  const slides = baseSlides.map((slide, index) => ({ ...slide, index }));
  return {
    topic: next.topic || current?.topic || "New deck",
    slideCount: slides.length,
    slides,
    reviewOperationId: current?.reviewOperationId,
    reviewOperationGeneration: current?.reviewOperationGeneration,
    reviewSkillBindingState: current?.reviewSkillBindingState,
    skillRunContext: current?.skillRunContext,
    planningEvidence: current?.planningEvidence,
    groundingMode: current?.groundingMode,
    groundingContextForSlides: current?.groundingContextForSlides,
    groundingMessage: current?.groundingMessage,
    groundingCitations: current?.groundingCitations,
  };
}

export async function executePresentDeckPlanTool(
  call: ToolCall,
  options: { agentToolContext?: AgentToolContext } = {}
): Promise<{ result: ToolResult }> {
  const plan = call.deckPlan;
  if (!plan || plan.slides.length === 0) {
    return {
      result: {
        tool: "present_deck_plan",
        success: false,
        error: "present_deck_plan requires a topic and at least one slide.",
      },
    };
  }
  const hydrated = await hydrateDeckPlanLibraryFields(plan, options.agentToolContext);
  try {
    await options.agentToolContext?.onPresentDeckPlan?.(hydrated);
  } catch (error) {
    return {
      result: {
        tool: "present_deck_plan",
        success: false,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
  const nudge = shouldNudgeLibraryPicks(hydrated, options.agentToolContext)
    ? ` ${libraryPickNudgeMessage()}`
    : "";
  return {
    result: {
      tool: "present_deck_plan",
      success: true,
      data: {
        presented: true,
        topic: hydrated.topic,
        slideCount: hydrated.slides.length,
        message:
          "Review Slide Outline is open. Do not call apply_edits or insert_library_slide until the user confirms Create slides. Use update_deck_plan if they ask to change the plan." +
          nudge,
      },
    },
  };
}

export const REVIEW_SESSION_CLOSED_UPDATE_ERROR =
  "Review is not open. The outline was cancelled or already confirmed. Do not call update_deck_plan.";

function isReviewSessionClosed(ctx?: AgentToolContext): boolean {
  return typeof ctx?.isReviewSessionOpen === "function" && !ctx.isReviewSessionOpen();
}

function closedReviewUpdateResult(): { result: ToolResult } {
  return {
    result: {
      tool: "update_deck_plan",
      success: false,
      error: REVIEW_SESSION_CLOSED_UPDATE_ERROR,
    },
  };
}

/** True while Review is actually open; falls back to the turn-scoped block flag. */
export function isReviewBlockingDeckBuild(ctx?: AgentToolContext): boolean {
  if (typeof ctx?.isReviewSessionOpen === "function") {
    return ctx.isReviewSessionOpen();
  }
  return Boolean(ctx?.blockNewSlideCreation);
}

export async function executeUpdateDeckPlanTool(
  call: ToolCall,
  options: { agentToolContext?: AgentToolContext } = {}
): Promise<{ result: ToolResult }> {
  const ctx = options.agentToolContext;
  if (isReviewSessionClosed(ctx)) {
    return closedReviewUpdateResult();
  }
  const live = ctx?.getPendingDeckPlan?.() ?? ctx?.pendingDeckPlan;
  const agentSnapshot = ctx?.pendingDeckPlan;
  const incoming = {
    topic: call.deckPlan?.topic,
    slides: call.deckPlan?.slides,
    patches: call.deckPlanPatches,
  };
  const merged = mergeDeckPlan(live, incoming, { base: agentSnapshot });
  if (merged.slides.length === 0) {
    return {
      result: {
        tool: "update_deck_plan",
        success: false,
        error: "update_deck_plan requires slides or patches against the current plan.",
      },
    };
  }
  const hydrated = await hydrateDeckPlanLibraryFields(merged, ctx);
  if (isReviewSessionClosed(ctx)) {
    return closedReviewUpdateResult();
  }
  const applied = ctx?.onUpdateDeckPlan?.(hydrated);
  if (applied === false) {
    return closedReviewUpdateResult();
  }
  // Keep pendingDeckPlan as the last agent outline, not the live-merged Review
  // copy. Host callbacks that write the merged plan back must not become `base`
  // for the next echo of the original titles/bullets.
  if (ctx) {
    ctx.pendingDeckPlan = mergeDeckPlan(agentSnapshot, incoming);
  }
  const nudge = shouldNudgeLibraryPicks(hydrated, ctx) ? ` ${libraryPickNudgeMessage()}` : "";
  return {
    result: {
      tool: "update_deck_plan",
      success: true,
      data: {
        updated: true,
        topic: hydrated.topic,
        slideCount: hydrated.slides.length,
        slides: hydrated.slides,
        message: "Plan updated in the Review dialog. Wait for Create slides." + nudge,
      },
    },
  };
}

export function buildPendingDeckPlanPrompt(plan: DeckPlan): string {
  const compactSlides = plan.slides.map((slide) => ({
    index: slide.index,
    title: slide.title,
    bullets: slide.bullets,
    librarySlideId: slide.librarySlideId,
    librarySlideTitle: slide.librarySlideTitle,
    baseSlideIndex: slide.baseSlideIndex,
    librarySlideCandidates: slide.librarySlideCandidates?.map((candidate) => ({
      slideId: candidate.slideId,
      title: candidate.title,
    })),
  }));
  return `CURRENT DECK PLAN (unconfirmed Review outline). Honor this plan as the source of truth until Create slides. Use update_deck_plan to change titles, bullets, librarySlideId, or baseSlideIndex. If the user wants another listed librarySlideCandidates match, patch that librarySlideId only — do not search. Search / get_library_slide_previews only when the requested template is not in that list. Empty librarySlideId string = FREEFORM. Do NOT call apply_edits or insert_library_slide to start building until the user confirms Create slides on the Review dialog.

${JSON.stringify({ topic: plan.topic, slideCount: plan.slides.length, slides: compactSlides }, null, 2)}`;
}
